import { useState, useEffect, useRef } from 'react';
import { ordersAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ChevronDown, ChevronUp, Calculator, RotateCcw, AlertTriangle, Check } from 'lucide-react';

export function ConsumptionBreakdown({
  garmentType,
  fabricId,
  measurementSetId,
  currentQty,
  onQuantityChange,
  cs = '\u20B9',
  pricePerMetre = 0,
}) {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overridden, setOverridden] = useState(false);
  const prevCalcRef = useRef(null);

  useEffect(() => {
    if (!fabricId && !garmentType) return;

    const calc = async () => {
      setLoading(true);
      try {
        const res = await ordersAPI.calculateConsumption({
          garment_type: garmentType,
          fabric_id: fabricId || null,
          measurement_set_id: measurementSetId || null,
        });
        setBreakdown(res.data);
        // Auto-update quantity unless user manually overrode
        if (!overridden && res.data.total_metres !== prevCalcRef.current) {
          onQuantityChange(res.data.total_metres);
          prevCalcRef.current = res.data.total_metres;
        }
      } catch (err) {
        console.error('Consumption calc failed', err);
      }
      setLoading(false);
    };
    calc();
  }, [garmentType, fabricId, measurementSetId]);

  const handleOverride = (val) => {
    setOverridden(true);
    onQuantityChange(val);
  };

  const handleResetToCalc = () => {
    if (breakdown) {
      setOverridden(false);
      onQuantityChange(breakdown.total_metres);
    }
  };

  if (!breakdown && !loading) return null;

  const isOverridden = overridden && breakdown && currentQty !== breakdown.total_metres;
  const fabricCost = currentQty * pricePerMetre;

  return (
    <div className="mt-2 rounded-lg border"
      style={{ borderColor: expanded ? 'rgba(197,165,90,0.4)' : 'rgba(27,42,74,0.08)' }}
      data-testid={`consumption-breakdown-${garmentType}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid={`consumption-toggle-${garmentType}`}
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5" style={{ color: 'var(--brand-gold)' }} />
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--brand-navy)' }}>
            Fabric Calculator
          </span>
          {loading && <span className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>calculating...</span>}
          {!loading && breakdown && (
            <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
              {breakdown.total_metres}m
            </span>
          )}
          {breakdown?.needs_pattern_match && (
            <Badge variant="outline" className="text-[8px] px-1 py-0 rounded-sm"
              style={{ borderColor: 'rgba(245,158,11,0.3)', color: '#D97706' }}>
              +{breakdown.pattern_extra_pct}% pattern
            </Badge>
          )}
          {isOverridden && (
            <Badge variant="outline" className="text-[8px] px-1 py-0 rounded-sm"
              style={{ borderColor: 'rgba(37,99,235,0.3)', color: '#2563EB' }}>
              overridden
            </Badge>
          )}
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
      </button>

      {expanded && breakdown && (
        <div className="px-3 pb-3 space-y-3">
          {/* Measurement source indicator */}
          <div className="flex items-center gap-1.5">
            {breakdown.measurement_source === 'client' ? (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md gap-0.5"
                style={{ borderColor: 'rgba(16,185,129,0.3)', color: '#059669' }}>
                <Check className="w-2.5 h-2.5" /> Client measurements
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md gap-0.5"
                style={{ borderColor: 'rgba(245,158,11,0.3)', color: '#D97706' }}>
                <AlertTriangle className="w-2.5 h-2.5" /> Size chart estimate
              </Badge>
            )}
            <span className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>
              Fabric width: {breakdown.fabric_width_cm}cm
            </span>
          </div>

          {/* Component table */}
          <div className="rounded-md border overflow-hidden" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                  <th className="text-left px-2 py-1.5 font-medium" style={{ color: 'var(--ink-muted)' }}>Component</th>
                  <th className="text-right px-2 py-1.5 font-medium" style={{ color: 'var(--ink-muted)' }}>Formula</th>
                  <th className="text-right px-2 py-1.5 font-medium" style={{ color: 'var(--ink-muted)' }}>Area</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.components.map((c, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                    <td className="px-2 py-1.5 font-medium" style={{ color: 'var(--navy-ink)' }}>{c.component}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: 'var(--ink-muted)' }}>{c.formula}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>{c.area_sq_cm.toLocaleString()}cm&sup2;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="space-y-1">
            <div className="flex justify-between text-[10px]">
              <span style={{ color: 'var(--ink-muted)' }}>Total area</span>
              <span className="tabular-nums" style={{ color: 'var(--navy-ink)' }}>{breakdown.total_area_sq_cm.toLocaleString()}cm&sup2;</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: 'var(--ink-muted)' }}>Base length ({breakdown.fabric_width_cm}cm width)</span>
              <span className="tabular-nums" style={{ color: 'var(--navy-ink)' }}>{breakdown.base_metres}m</span>
            </div>
            {breakdown.needs_pattern_match && (
              <div className="flex justify-between text-[10px]">
                <span style={{ color: '#D97706' }}>Pattern matching (+{breakdown.pattern_extra_pct}%)</span>
                <span className="tabular-nums" style={{ color: '#D97706' }}>+{breakdown.pattern_extra_metres}m</span>
              </div>
            )}
            <div className="flex justify-between text-[10px]">
              <span style={{ color: 'var(--ink-muted)' }}>Wastage (+{breakdown.wastage_pct}%)</span>
              <span className="tabular-nums" style={{ color: 'var(--ink-muted)' }}>+{breakdown.wastage_metres}m</span>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between text-xs font-semibold">
              <span style={{ color: 'var(--brand-navy)' }}>Calculated Total</span>
              <span className="tabular-nums" style={{ color: 'var(--brand-navy)' }}>{breakdown.total_metres}m</span>
            </div>
            {pricePerMetre > 0 && (
              <div className="flex justify-between text-[10px]">
                <span style={{ color: 'var(--ink-muted)' }}>Fabric cost ({cs}{pricePerMetre}/m x {currentQty}m)</span>
                <span className="font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                  {cs}{Math.round(fabricCost).toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>

          {/* Override controls */}
          <div className="flex items-center gap-2 pt-1">
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}>Override:</span>
              <Input
                type="number"
                step="0.1"
                value={currentQty}
                onChange={(e) => handleOverride(parseFloat(e.target.value) || 0)}
                className="h-7 w-20 text-xs rounded-md"
                data-testid={`consumption-override-${garmentType}`}
              />
              <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>m</span>
            </div>
            {isOverridden && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[9px] gap-1" onClick={handleResetToCalc}
                data-testid={`consumption-reset-${garmentType}`}>
                <RotateCcw className="w-2.5 h-2.5" /> Use calculated
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
